import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import FadeUp from "../components/FadeUp";
import { useLang } from "../i18n";
import { useConsent } from "../components/CookieBanner";
import { api, formatApiError } from "../lib/api";

export default function Appointments() {
  const { t, lang } = useLang();
  const { consent, decide } = useConsent();
  const location = useLocation();
  const [form, setForm] = useState({
    name: "", email: "", phone: "", appointment_type: t("appointments.typeInitial"),
    preferred_date: "", message: "",
  });
  const [consentChecked, setConsentChecked] = useState(false);
  const [status, setStatus] = useState("idle");

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !consentChecked) return setStatus("error");
    setStatus("sending");
    try {
      await api.post("/inquiries", { type: "appointment", ...form, language: lang, dress_id: location.state?.dressId || null });
      setStatus("sent");
    } catch (err) {
      formatApiError(err.response?.data?.detail);
      setStatus("error");
    }
  };

  const types = [t("appointments.typeInitial"), t("appointments.typeBespoke"), t("appointments.typeFitting"), t("appointments.typeRental")];

  return (
    <div data-testid="appointments-page">
      <section className="pt-40 pb-16 lg:pt-52 max-w-[1440px] mx-auto px-6 lg:px-12">
        <FadeUp><h1 className="font-serif font-light text-5xl sm:text-6xl lg:text-7xl">{t("appointments.title")}</h1></FadeUp>
        <FadeUp delay={0.1}><p className="mt-6 max-w-xl text-[15px] leading-relaxed text-stone">{t("appointments.sub")}</p></FadeUp>
      </section>

      <section className="max-w-[1440px] mx-auto px-6 lg:px-12 pb-24 grid gap-16 lg:grid-cols-12">
        <div className="lg:col-span-7" data-testid="appointment-form">
          <FadeUp>
            <h2 className="font-serif text-3xl mb-10">{t("appointments.formTitle")}</h2>
            {status === "sent" ? (
              <p className="font-serif text-2xl text-gold py-16" data-testid="appointment-success">{t("appointments.sent")}</p>
            ) : (
              <form onSubmit={submit} className="space-y-8">
                <div className="grid sm:grid-cols-2 gap-8">
                  <input className="field" data-testid="appointment-name" placeholder={t("appointments.name")} value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })} />
                  <input className="field" data-testid="appointment-email" type="email" placeholder={t("appointments.email")} value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </div>
                <input className="field" data-testid="appointment-phone" placeholder={t("appointments.phone")} value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                <select className="field" data-testid="appointment-type" value={form.appointment_type}
                  onChange={(e) => setForm({ ...form, appointment_type: e.target.value })}>
                  {types.map((ty) => <option key={ty} value={ty}>{ty}</option>)}
                </select>
                <textarea className="field" data-testid="appointment-message" rows="4" placeholder={t("appointments.message")} value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })} />
                {status === "error" && <p className="text-sm text-red-700" data-testid="appointment-error">{t("appointments.required")}</p>}
                <label className="flex items-start gap-3 text-sm text-stone cursor-pointer" data-testid="appointment-consent">
                  <input type="checkbox" className="mt-1 accent-[#C5A880]" checked={consentChecked}
                    onChange={(e) => setConsentChecked(e.target.checked)} data-testid="appointment-consent-checkbox" />
                  <span>{t("appointments.consent")}{" "}<Link to="/privacy" className="underline underline-offset-2 hover:text-gold">{t("privacy.navPrivacy")}</Link></span>
                </label>
                <button className="btn-solid" data-testid="appointment-submit" disabled={status === "sending" || !consentChecked}>
                  {status === "sending" ? "…" : t("appointments.send")}
                </button>
              </form>
            )}
          </FadeUp>
        </div>
        <div className="lg:col-span-4 lg:col-start-9" data-testid="appointment-contact">
          <FadeUp delay={0.1}>
            <p className="eyebrow mb-4">{t("appointments.visit")}</p>
            <p className="font-serif text-2xl">{t("appointments.address")}</p>
            <p className="mt-3 text-sm text-stone">{t("appointments.hours")}</p>
            <div className="mt-6 clip-frame h-48 sm:h-56 border border-softline" data-testid="contact-map">
              {consent === "accepted" ? (
                <iframe
                  src="https://maps.google.com/maps?q=Via%20Eleonora%20Fonseca%20Pimentel%2018%2C%20Milano&t=&z=16&ie=UTF8&iwloc=&output=embed"
                  className="w-full h-full border-0"
                  title="Mappa Per Lei Bridal"
                  loading="lazy"
                  allowFullScreen
                />
              ) : (
                <button onClick={() => decide("accepted")} data-testid="contact-map-enable"
                  className="w-full h-full flex items-center justify-center text-center px-6 text-sm text-stone hover:text-gold transition-colors bg-sand">
                  {t("privacy.enableMap")}
                </button>
              )}
            </div>
            <p className="mt-3 text-sm">
              <a href="https://www.instagram.com/per_lei_bridal_official/" target="_blank" rel="noopener noreferrer" className="hover:text-gold transition-colors" data-testid="contact-instagram">Instagram: @per_lei_bridal_official</a>
            </p>
            <p className="mt-2 text-sm">
              <span className="hover:text-gold transition-colors" data-testid="contact-wechat">WeChat: PerLeiBridal</span>
            </p>
            <p className="mt-6 text-sm">
              <a href="mailto:info@perleibridal.com" className="hover:text-gold transition-colors" data-testid="contact-email">info@perleibridal.com</a>
            </p>
            <p className="mt-2 text-sm">
              <a href="tel:+393514728860" className="hover:text-gold transition-colors" data-testid="contact-phone">+39 3514728860</a>
            </p>
          </FadeUp>
        </div>
      </section>

      <section className="max-w-[1440px] mx-auto px-6 lg:px-12 py-24 lg:py-32" data-testid="prepare-section">
        <FadeUp><p className="eyebrow mb-6">{t("appointments.prepareTitle")}</p></FadeUp>
        <FadeUp delay={0.1}><p className="font-serif text-2xl sm:text-3xl max-w-2xl leading-relaxed text-charcoal/90">{t("appointments.prepareSub")}</p></FadeUp>
        <div className="mt-16 grid gap-12 lg:grid-cols-2">
          <div>
            <p className="eyebrow mb-6">{t("appointments.prepareForHer")}</p>
            <ol className="space-y-6">
              {[
                { it: ["Un look che ti rappresenti", "Se ti va, vieni con un trucco leggero e porta un elastico o una pinza per raccogliere i capelli. Ti aiuterà a immaginare meglio il tuo look da sposa!"], en: ["A look that represents you", "Feel free to come with light makeup and bring an elastic or clip to gather your hair. It will help you imagine your bridal look better!"], zh: ["展现自我的造型", "可以带淡妆前来，并带上发圈或发夹。这样更能想象新娘造型！"] },
                { it: ["L'intimo giusto", "Ti consigliamo copricapezzoli adesivi e biancheria color nude. Se ami gli abiti a sirena, uno slip modellante può fare la differenza!"], en: ["The right underwear", "We recommend adhesive nipple covers and nude underwear. If you love mermaid dresses, a shaping slip can make a difference!"], zh: ["合适的内衣", "建议穿贴乳贴和裸色内衣。如果您喜欢鱼尾款，塑身内裤会让效果更好！"] },
                { it: ["Scarpe e accessori", "Hai già scelto le scarpe o gli accessori? Portali con te! Altrimenti, nessun problema."], en: ["Shoes and accessories", "Have you already chosen shoes or accessories? Bring them with you! Otherwise, no problem."], zh: ["鞋子与配饰", "如果您已选好鞋子或配饰，请带上！没有也没关系。"] },
                { it: ["Prenditi il tuo tempo", "La prova dura circa due ore. Ti consigliamo di mangiare qualcosa prima di arrivare, così potrai goderti ogni momento senza fretta."], en: ["Take your time", "The fitting lasts about two hours. We recommend eating something before you arrive, so you can enjoy every moment without rushing."], zh: ["从容不迫", "试纱约两小时。建议提前吃点东西，这样可以安心享受每一刻。"] },
                { it: ["Le persone del cuore", "Puoi portare con te fino a 3 accompagnatori. Scegli le persone con cui ti senti più a tuo agio!"], en: ["Your closest people", "You can bring up to 3 companions. Choose the people you feel most comfortable with!"], zh: ["至亲之人", "您可以带最多3位陪同。选择让您最自在的人！"] },
              ].map((item, i) => (
                <li key={i} className="flex gap-5 border-t border-softline pt-5" data-testid={`prepare-her-${i + 1}`}>
                  <span className="text-[11px] tracking-[0.3em] text-gold pt-1">0{i + 1}</span>
                  <div><p className="font-serif text-xl">{item[lang][0]}</p><p className="text-sm text-stone mt-1 leading-relaxed">{item[lang][1]}</p></div>
                </li>
              ))}
            </ol>
          </div>
          <FadeUp delay={0.15}>
            <article className="bg-warm p-8 h-full">
              <p className="eyebrow mb-4">{t("appointments.prepareForHim")}</p>
              <p className="text-[15px] leading-relaxed text-stone">{t("appointments.prepareForHimBody")}</p>
            </article>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
